import { useEffect, useRef } from "react";
import { Box, Button, Divider, Typography } from "@mui/material";
import cn from "classnames";
import ChatMessage, { ChatMessageProps } from "./ChatMessage";
import ChatMessageOffline from "./ChatMessageOffline";

import useStyles from "../Chatbot.styles";
import { useAppziWidget } from "utils";
import ChatbotLoading from "./ChatbotLoading";
import { useIntl } from "react-intl";

interface ChatbotBodyProps {
    messages: ChatMessageProps[];
    isLoading: boolean;
    isOnline: boolean;
    shouldFillHeight: boolean;
}

const BOTTOM_SCROLL_THRESHOLD = 8;

const ChatbotBody = ({ messages, isLoading, isOnline, shouldFillHeight }: ChatbotBodyProps) => {
    const classes = useStyles();
    const intl = useIntl();

    const messagesContainerRef = useRef<HTMLDivElement | null>(null);
    const responseEndRef = useRef<HTMLDivElement | null>(null);
    const shouldAutoScrollRef = useRef(true);

    const { handleOpenWidget } = useAppziWidget();

    const isScrolledToBottom = () => {
        const messagesContainer = messagesContainerRef.current;

        if (!messagesContainer) {
            return true;
        }

        const distanceFromBottom =
            messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight;

        return distanceFromBottom <= BOTTOM_SCROLL_THRESHOLD;
    };

    const handleMessagesScroll = () => {
        shouldAutoScrollRef.current = isScrolledToBottom();
    };

    useEffect(() => {
        if (!isOnline || !shouldAutoScrollRef.current) {
            return;
        }

        responseEndRef.current?.scrollIntoView({ block: "end" });
    }, [messages, isLoading, isOnline]);

    return (
        <Box
            className={cn(classes.body, {
                [classes.bodyFillHeight]: shouldFillHeight,
            })}
        >
            {isOnline ? (
                <>
                    <Box
                        ref={messagesContainerRef}
                        className={classes.messagesContainer}
                        aria-label="Chat messages"
                        aria-live="polite"
                        aria-relevant="additions text"
                        onScroll={handleMessagesScroll}
                    >
                        {messages
                            .filter((msg) => msg.type === "user" || msg.content.trim().length > 1)
                            .map((msg: ChatMessageProps) => (
                                <ChatMessage key={msg.id} {...msg} />
                            ))}
                        {isLoading && <ChatbotLoading isFirstLoad={messages.length === 1} />}
                        <Box ref={responseEndRef} />
                    </Box>
                    <Box className={classes.feedbackContainer}>
                        <Divider />
                        <Box className={classes.feedbackContent}>
                            <Typography>{intl.formatMessage({ id: "chatbot.feedback" })}</Typography>
                            <Button
                                variant="text"
                                size="small"
                                className={classes.feedbackButton}
                                aria-label={intl.formatMessage({ id: "chatbot.ariaLabel.sendFeedback" })}
                                onClick={handleOpenWidget}
                            >
                                {intl.formatMessage({ id: "chatbot.feedback.button" })}
                            </Button>
                        </Box>
                    </Box>
                </>
            ) : (
                <ChatMessageOffline />
            )}
        </Box>
    );
};

export default ChatbotBody;
